import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import http from "../utils/http";
import { errorToastMessage, toastMessage } from "../utils/toast";
import {
  FROZEN_COLUMNS_PREFERENCE_KEYS,
  FrozenColumnsListKey,
  MAX_FROZEN_COLUMNS,
  getStudyDefaultFrozenKeys,
  sanitizeFrozenKeys,
} from "../utils/frozenColumns";

type UseFrozenColumnsOptions = {
  listKey: FrozenColumnsListKey;
  availableColumnKeys: string[];
  /** Wait until true before first init (e.g. participant list fetch done so custom columns exist). */
  columnsReady?: boolean;
};

export function useFrozenColumns({
  listKey,
  availableColumnKeys,
  columnsReady = true,
}: UseFrozenColumnsOptions) {
  const preferenceKey = FROZEN_COLUMNS_PREFERENCE_KEYS[listKey];
  const [frozenKeys, setFrozenKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const savedKeysRef = useRef<string[] | null | undefined>(undefined);
  const hasLoadedRef = useRef(false);
  const hasInitializedRef = useRef(false);
  const skipNextSaveRef = useRef(false);

  const defaultKeys = useMemo(() => {
    if (listKey === "study") {
      return getStudyDefaultFrozenKeys(availableColumnKeys);
    }
    return [];
  }, [listKey, availableColumnKeys]);

  const savePreference = useMemo(
    () =>
      debounce(async (keys: string[]) => {
        try {
          await http.put(`/users/me/preferences/${preferenceKey}`, {
            frozenColumnKeys: keys,
          });
          savedKeysRef.current = keys;
        } catch (err) {
          errorToastMessage(err as Error);
        }
      }, 300),
    [preferenceKey],
  );

  useEffect(() => {
    return () => {
      savePreference.cancel();
    };
  }, [savePreference]);

  useEffect(() => {
    const fetchPreference = async () => {
      try {
        setLoading(true);
        const res = await http.get(`/users/me/preferences/${preferenceKey}`);
        savedKeysRef.current = res.data?.data?.frozenColumnKeys ?? null;
      } catch {
        savedKeysRef.current = null;
      } finally {
        hasLoadedRef.current = true;
        setLoading(false);
      }
    };

    fetchPreference();
  }, [preferenceKey]);

  useEffect(() => {
    if (
      !hasLoadedRef.current ||
      !columnsReady ||
      availableColumnKeys.length === 0
    ) {
      return;
    }

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const source =
        savedKeysRef.current === null || savedKeysRef.current === undefined
          ? defaultKeys
          : savedKeysRef.current;
      skipNextSaveRef.current = true;
      setFrozenKeys(sanitizeFrozenKeys(source, availableColumnKeys));
      return;
    }

    setFrozenKeys((prev) => {
      const sanitized = sanitizeFrozenKeys(prev, availableColumnKeys);
      if (
        sanitized.length === prev.length &&
        sanitized.every((key, index) => key === prev[index])
      ) {
        return prev;
      }
      return sanitized;
    });
  }, [availableColumnKeys, columnsReady, defaultKeys]);

  useEffect(() => {
    if (!hasLoadedRef.current || !hasInitializedRef.current) {
      return;
    }

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    savePreference(frozenKeys);
  }, [frozenKeys, savePreference]);

  const toggleFreeze = useCallback((key: string) => {
    setFrozenKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((item) => item !== key);
      }

      if (prev.length >= MAX_FROZEN_COLUMNS) {
        toastMessage(
          "warning",
          `Maximum ${MAX_FROZEN_COLUMNS} columns can be frozen`,
        );
        return prev;
      }

      return [...prev, key];
    });
  }, []);

  const clearFrozen = useCallback(() => {
    setFrozenKeys([]);
  }, []);

  const isAtMax = frozenKeys.length >= MAX_FROZEN_COLUMNS;

  return {
    frozenKeys,
    toggleFreeze,
    clearFrozen,
    isAtMax,
    loading,
  };
}
